/**
 * Lens dispensing: what the dispensing dialogs, the dashboard tile and the practice job list share.
 *
 * Jobs are kept by the REST API (LensDispensingAPI) and read here into the shape the dialogs edit;
 * each job is made from an issued prescription (LensPrescribingStore) and keeps its own copy of it.
 * Spectacle lenses and made-to-order contact lenses come from the practice's lens catalogue
 * (LensCatalogueAPI) - suppliers publish no catalogue API, so each practice keeps its own, priced from
 * its suppliers' lists. Frames and stock contact lenses come from practice inventory, and the server
 * takes a job's frame, or its boxes of contact lenses, off stock when its invoice is approved. A
 * practice without inventory - or whose inventory can't be read - enters the frame by hand, and
 * prescribes stock contact lenses from its lens catalogue: nothing there waits on inventory.
 *
 * A practice billing through Xero raises each job's invoice there. When Xero can't be used (not logged
 * in, say) the invoice can be kept in CatTrax and raised in Xero later, so dispensing never waits on it.
 *
 * The job extras - lens types, tints, coatings, mirror finishes, add-ons, and for contact lenses the
 * fitting fees, care products and delivery - and their prices are the practice's own, set in its
 * dispensing settings (PracticeService) and read here. A job refers to an extra by id and is priced
 * from the settings until it is invoiced; the invoice keeps the lines it was sent with, so a price
 * changed later never alters it. The labs orders go to are still fixed here.
 */
app.service("LensDispensingStore", [
	"$q",
	"$rootScope",
	"countryConfig",
	"Session",
	"PatientAPI",
	"LensDispensingAPI",
	"LensCatalogueAPI",
	"UploadFileAPI",
	"PracticeService",
	"InventoryAPI",
	"XeroItemCode",
	"BillingAPI",
	function ($q, $rootScope, countryConfig, Session, PatientAPI, LensDispensingAPI, LensCatalogueAPI, UploadFileAPI, PracticeService, InventoryAPI, XeroItemCode, BillingAPI) {
		// ------------------------------------------------------------------
		// Frames from practice inventory
		// ------------------------------------------------------------------
		// The frame step lists the practice's FRAME inventory items. An inventory item has a name, a
		// price and its variations (colour, size, stock, barcode) - no brand, shape, material or colour
		// swatch - so the drawing is guessed from the words in the name and colour, falling back to a
		// plain rectangle in grey.
		const FRAME_SHAPE_WORDS = [
			["rimless", /rimless|semi-?rimless|drill/],
			["browline", /browline|clubmaster|clubround/],
			["aviator", /aviator|pilot/],
			["cateye", /cat-?\s?eye/],
			["round", /round|panto|lemtosh/],
			["oval", /oval/],
			["square", /square/],
		];
		const FRAME_MATERIAL_WORDS = [
			["Rimless", /rimless|semi-?rimless|drill/],
			["Titanium", /titanium|\bti\b/],
			["Metal", /metal|steel|aviator|wire/],
			["Acetate", /acetate|plastic|tr-?90/],
		];
		// First match wins: "rose gold" before "gold", "navy" before "blue", and the finishes
		// (crystal, clear, matte) last, so "Crystal blue" is blue and only "Crystal" alone is clear.
		const FRAME_COLOUR_WORDS = [
			["rose gold", "#b77a6a"], ["gunmetal", "#55595f"], ["tortoise", "#5a3a22"], ["havana", "#7a4a24"],
			["burgundy", "#7b1f3a"], ["champagne", "#c9b28a"], ["bronze", "#8c6239"], ["copper", "#a8643a"],
			["silver", "#9aa1a9"], ["purple", "#6b3f8f"], ["orange", "#d9772b"], ["blonde", "#c99a5b"],
			["black", "#1f2329"], ["white", "#e9e9e6"], ["brown", "#5b3b26"], ["amber", "#b8742a"],
			["honey", "#c08a3e"], ["olive", "#5d6b3a"], ["green", "#3d7a4a"], ["navy", "#1d3461"],
			["blue", "#5d8fc9"], ["gold", "#c2a14d"], ["grey", "#6b7078"], ["gray", "#6b7078"],
			["pink", "#d9a3a6"], ["teal", "#1f7a7a"], ["red", "#b3242b"], ["tan", "#b08a5c"],
			["crystal", "#cfdde8"], ["clear", "#d8e3ea"], ["matte", "#3a3d42"],
		];
		const FRAME_COLOUR_FALLBACK = "#8a8f98";

		const firstMatch = (table, text) => (table.find((row) => row[1].test(text)) || [])[0] || null;
		// Whole words only, so "Standard" is not tan.
		const frameColourHex = (name) => {
			const text = (name || "").toLowerCase();
			return (FRAME_COLOUR_WORDS.find((row) => new RegExp(`\\b${row[0]}\\b`).test(text)) || [])[1] || FRAME_COLOUR_FALLBACK;
		};

		// An InventoryAPI item, with its variations, as a frame tile: one swatch per variation still in
		// use. Two variations in the same colour and different sizes get their size in the swatch name.
		function frameFromInventory(item) {
			const name = (item.name || "").trim();
			const text = name.toLowerCase();
			const variations = InventoryAPI.activeVariations(item);
			const sameColour = (v) => variations.filter((o) => (o.color || "") === (v.color || "")).length > 1;
			return {
				id: item.item_id,
				brand: "",
				model: name,
				shape: firstMatch(FRAME_SHAPE_WORDS, text) || "rectangle",
				material: firstMatch(FRAME_MATERIAL_WORDS, text) || "",
				price: item.price || 0,
				fromInventory: true,
				colours: variations.map((v) => {
					const colour = (v.color || "").trim() || "Standard";
					return {
						name: sameColour(v) && v.size ? `${colour} · ${v.size}` : colour,
						hex: frameColourHex(colour),
						variationId: v.variation_id,
						size: v.size || "",
						stock: v.quantity || 0,
						barcode: v.barcode || "",
						location: v.location_name || "",
					};
				}),
			};
		}

		// Point a job's frame at one of its colours. An inventory frame's colours are its variations,
		// each with its own size, stock and barcode, so those follow the colour.
		function setFrameColour(frame, colour) {
			frame.colour = colour;
			if (!frame.fromInventory) return;
			frame.size = colour.size;
			frame.stock = colour.stock;
			frame.sku = colour.barcode || null;
			frame.inventory = { itemId: frame.id, variationId: colour.variationId };
		}

		const frameName = (frame) =>
			!frame ? "" : frame.own ? ["Patient's own frame", frame.model].filter(Boolean).join(" - ") : [frame.brand, frame.model].filter(Boolean).join(" ");

		// The patient's own frame, glazed with new lenses: no inventory item, no stock, and no frame on the
		// invoice unless the practice charges for fitting lenses to it. Its description, colour and shape
		// are the dispenser's own words.
		const OWN_FRAME_ID = "own-frame";
		function ownFrame() {
			const colour = { name: "", hex: frameColourHex("") };
			return { id: OWN_FRAME_ID, own: true, brand: "", model: "", shape: "rectangle", material: "", price: 0, fromInventory: false, colours: [], colour: colour, size: "" };
		}

		// A frame the practice sells that isn't in inventory - it has none, or it can't be read just now. Named
		// and priced by the dispenser, and never taken off stock.
		const OTHER_FRAME_ID = "other-frame";
		function otherFrame() {
			return angular.extend(ownFrame(), { id: OTHER_FRAME_ID, own: false, manual: true });
		}

		// ------------------------------------------------------------------
		// Lens designs, tint densities, supply lengths and labs (the priced extras are the practice's
		// dispensing settings, below)
		// ------------------------------------------------------------------
		const LENS_DESIGNS = [
			{ key: "single", label: "Single vision", hint: "One focal power - distance or reading" },
			{ key: "progressive", label: "Progressive", hint: "Distance to near, no line" },
			{ key: "bifocal", label: "Bifocal", hint: "Distance with a reading segment" },
			{ key: "office", label: "Office", hint: "Screen and desk distance" },
		];

		const TINT_DENSITIES = [25, 50, 75, 85];

		const LABS = {
			Essilor: "Essilor NZ - Auckland lab",
			Hoya: "Hoya Lens NZ - Wellington",
			Zeiss: "Zeiss Vision Care - Auckland",
			Shamir: "Shamir NZ - Christchurch",
		};

		const CL_MODALITIES = [
			{ key: "daily", label: "Daily", days: 1, hint: "New pair every day - no solution" },
			{ key: "fortnightly", label: "2-weekly", days: 14, hint: "Replace every two weeks" },
			{ key: "monthly", label: "Monthly", days: 30, hint: "Replace every month" },
			{ key: "custom", label: "Made to order", days: 365, hint: "Hard lenses made by a lab for this patient" },
		];

		const CL_DESIGNS = [
			{ key: "spherical", label: "Spherical", hint: "Short or long sight" },
			{ key: "toric", label: "Toric", hint: "Corrects astigmatism" },
			{ key: "multifocal", label: "Multifocal", hint: "Distance and near" },
			{ key: "orthok", label: "Ortho-K", hint: "Worn overnight to reshape the cornea" },
			{ key: "scleral", label: "Scleral", hint: "Large lens that vaults the cornea" },
		];

		// Handling tints a hard lens can be made in, so it can be found when dropped.
		const CL_COLOURS = [
			{ name: "Blue", hex: "#7fa6dc" },
			{ name: "Green", hex: "#8ec9a3" },
			{ name: "Grey", hex: "#a6adb6" },
			{ name: "Clear", hex: null },
		];

		// A hard lens made to order by a lab for this patient, described in the prescriber's own words - as
		// the patient's own frame is in glasses dispensing - rather than picked from the lens catalogue. Its
		// ranges are as wide as any lab makes: the lab checks the specification it is sent.
		const MADE_TO_ORDER_ID = "made-to-order";
		function madeToOrderLens() {
			return {
				id: MADE_TO_ORDER_ID, madeToOrder: true, custom: true, brand: "", name: "", maker: "", modality: "custom",
				design: "spherical", family: null, material: "", materials: [], colours: CL_COLOURS.map((c) => c.name).reverse(),
				pack: 1, price: 0, cost: null, leadDays: 10, warrantyDays: null, replaceMonths: 12, features: [], bcs: [],
				range: [-30, 30], bcRange: [5, 11], diaRange: [7, 25], defaults: { bc: null, dia: null },
			};
		}

		const CL_SUPPLY_MONTHS = [3, 6, 12];
		const CL_SUPPLIERS = {
			"Johnson & Johnson": "J&J Vision - NZ distribution",
			Alcon: "Alcon NZ - Auckland",
			CooperVision: "CooperVision NZ - Auckland",
			Capricornia: "Capricornia contact lens lab",
			"Corneal Lens Corp": "Corneal Lens Corp - Christchurch",
		};

		const STATUSES = [
			{ key: "QUOTE", label: "Quote", icon: "fa-file-text-o" },
			{ key: "INVOICED", label: "Ready to order", icon: "fa-file-o" },
			{ key: "ORDERED", label: "Order sent", icon: "fa-paper-plane" },
			{ key: "IN_PRODUCTION", label: "At lab", icon: "fa-cogs" },
			{ key: "RECEIVED", label: "Ready to collect", icon: "fa-inbox" },
			{ key: "COLLECTED", label: "Collected", icon: "fa-check" },
		];

		// Contact lenses are stock items: the supplier dispatches rather than makes them, and an order
		// shipped to the patient is done when it is delivered - it never waits at the practice.
		const CL_LABELS = { IN_PRODUCTION: { label: "Dispatched", icon: "fa-truck" } };
		const CL_HOME_LABELS = angular.extend({}, CL_LABELS, { COLLECTED: { label: "Delivered", icon: "fa-home" } });

		// The progress track under a job, without the quote stage. Built once per pathway so the
		// templates repeat over stable arrays.
		const relabel = (labels) => (s) => angular.extend({}, s, labels[s.key] || {});
		const TRACKS = {
			glasses: STATUSES.slice(1),
			contacts: STATUSES.slice(1).map(relabel(CL_LABELS)),
			contactsHome: STATUSES.slice(1).filter((s) => s.key !== "RECEIVED").map(relabel(CL_HOME_LABELS)),
		};

		const gstRate = countryConfig.countryCode === "AU" ? 0.1 : 0.15;

		// ------------------------------------------------------------------
		// Dispensing extras (the practice's settings, from the API)
		// ------------------------------------------------------------------
		// Every item, offered or not, so a job that used one can still name and price it; the
		// `active` lists are what the dialogs offer. Empty until loadSettings() has read them.
		const EXTRAS = {
			lensTypes: [], tints: [], coatings: [], mirrors: [], accessories: [], fittingFees: [], careProducts: [],
			gradientPrice: 0, homeDeliveryPrice: 0, homeDeliveryFreeOver: null, annualSupplyDiscountPercent: 0,
		};
		const EXTRA_LISTS = ["lensTypes", "tints", "coatings", "mirrors", "accessories", "fittingFees", "careProducts"];
		const active = {};
		let delivery = [];
		let settingsPromise = null;
		let settingsPracticeId = null;

		function extraFromApi(i) {
			return {
				id: i.id, name: i.name, hint: i.hint || "", price: Number(i.price) || 0, active: i.active !== false, fixed: !!i.fixed,
				included: !!i.included, hex: i.colour || null, previewHex: i.colour || null, icon: i.icon || null,
				soft: i.suits === "soft", rigid: i.suits === "rigid", reusable: !!i.reusable,
			};
		}

		// Takes the practice's settings as the API sends them.
		function applySettings(r) {
			EXTRA_LISTS.forEach(function (list) {
				EXTRAS[list] = ((r && r[snakeCase(list)]) || []).map(extraFromApi);
				active[list] = EXTRAS[list].filter((i) => i.active);
			});
			EXTRAS.gradientPrice = Number(r && r.gradient_price) || 0;
			EXTRAS.homeDeliveryPrice = Number(r && r.home_delivery_price) || 0;
			EXTRAS.homeDeliveryFreeOver = r && r.home_delivery_free_over !== null && r.home_delivery_free_over !== undefined ? Number(r.home_delivery_free_over) : null;
			EXTRAS.annualSupplyDiscountPercent = Number(r && r.annual_supply_discount_percent) || 0;
			delivery = [
				{ key: "practice", label: "Collect from practice", icon: "fa-building-o", price: 0 },
				{ key: "home", label: "Ship to patient", icon: "fa-truck", price: EXTRAS.homeDeliveryPrice, freeOver: EXTRAS.homeDeliveryFreeOver },
			];
		}
		applySettings(null);

		function snakeCase(key) {
			return key.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
		}

		// The practice's settings, read once per practice. Saving them in practice settings reads them again.
		function loadSettings() {
			const practiceId = Session.practice && Session.practice.id;
			if (settingsPromise && settingsPracticeId === practiceId) return settingsPromise;
			settingsPracticeId = practiceId;
			settingsPromise = PracticeService.queryLensDispensingSettings(practiceId).then(
				(resp) => applySettings(resp.data),
				function (resp) {
					settingsPromise = null;
					return $q.reject(resp);
				}
			);
			return settingsPromise;
		}

		// An extra by id, whether or not it is still offered.
		const extra = (list, id) => byId(EXTRAS[list], id);

		// ------------------------------------------------------------------
		// Helpers
		// ------------------------------------------------------------------
		const byId = (list, id) => list.find((t) => t.id === id) || null;

		// One eye of a spectacle Rx: powers, intermediate and near ADDs, horizontal (In/Out) and vertical
		// (Up/Down) prisms, and the monocular PD and fitting height. A balance eye has no useful vision:
		// the lab makes a lens to match the other one for weight and look, so it has no powers.
		const emptyEye = () => ({ sph: null, cyl: null, axis: null, intAdd: null, add: null, hPrism: null, hBase: "", vPrism: null, vBase: "", pd: null, height: null, balance: false });

		// `pd` is the binocular PD, as the vision module records it; an eye's `pd` is its monocular PD.
		function emptyRx() {
			const eye = emptyEye;
			return { od: eye(), os: eye(), pd: null, use: "distance", expires: null, prescriber: "", prescriberRegistration: null, source: null };
		}

		// A cylinder written the other way round: the same lens with plus cylinder as minus, or minus as plus.
		// Contact lenses are only made in minus cylinder.
		function transpose(e) {
			if (!e || !isNum(e.sph) || !isNum(e.cyl) || Number(e.cyl) === 0) return e;
			const axis = isNum(e.axis) ? ((Number(e.axis) + 90 - 1) % 180) + 1 : e.axis;
			return angular.extend({}, e, { sph: Number(e.sph) + Number(e.cyl), cyl: -Number(e.cyl), axis: axis });
		}
		const toMinusCyl = (e) => (e && isNum(e.cyl) && Number(e.cyl) > 0 ? transpose(e) : e);

		// The coatings the practice puts on every lens start on a new job.
		function emptyExtras() {
			const included = active.coatings.filter((c) => c.included).map((c) => c.id);
			return { tint: "clear", density: 50, gradient: false, lensType: "standard", coatings: included, mirror: "none", accessories: [] };
		}

		// ------------------------------------------------------------------
		// Xero item codes
		// ------------------------------------------------------------------
		// Each thing sold is its own Xero item, made the first time it is invoiced - see XeroItemCode.
		const itemCode = XeroItemCode.make;

		// ------------------------------------------------------------------
		// Billing items, for the hand-added invoice lines
		// ------------------------------------------------------------------
		// The same search as the invoice dialog: the practice's Xero items, and - where inventory is
		// on and the user may manage it - its inventory. Xero's items are read once; when they can't
		// be (not logged in, say) the search finds nothing and the name is simply typed.
		let billingItemsPromise = null;
		function xeroBillingItems() {
			if (!billingItemsPromise) {
				billingItemsPromise = BillingAPI.fetchBillingItems().then(
					(resp) => resp.data || [],
					function () {
						billingItemsPromise = null;
						return [];
					}
				);
			}
			return billingItemsPromise;
		}

		const searchesInventory = () =>
			!!(Session.practice && Session.practice.practiceFeatures && Session.practice.practiceFeatures.inventoryFeature) &&
			Session.hasPermission("PRACTICE_INVENTORY_MANAGE");

		function billingItemFromInventory(item) {
			return {
				code: XeroItemCode.forInventoryItem(item),
				name: item.name,
				unit_price: item.price,
				fromInventory: true,
				categoryLabel: InventoryAPI.CATEGORY_LABELS[item.category] || "Inventory",
			};
		}

		function searchBillingItems(term) {
			const t = (term || "").trim().toLowerCase();
			const matches = (i) => !t || ["code", "name", "description"].some((f) => (i[f] || "").toLowerCase().indexOf(t) !== -1);
			const xero = xeroBillingItems().then((items) => items.filter(matches).slice(0, 10));
			if (!searchesInventory() || t.length < 2) return xero;
			const inventory = InventoryAPI.searchItems((term || "").trim()).then(
				(resp) => ((resp.data && resp.data.items) || []).slice(0, 10).map(billingItemFromInventory),
				() => []
			);
			// An inventory item and the Xero item it bills under are the same thing: offered once, from inventory.
			return $q.all([xero, inventory]).then(function (results) {
				const codes = results[1].map((i) => i.code);
				return results[0].filter((i) => codes.indexOf(i.code) === -1).concat(results[1]);
			});
		}

		// The typeahead also calls this with no item, or with the picked name, to work out what the box shows.
		const billingItemLabel = (i) => (!i || typeof i !== "object" ? i || "" : i.fromInventory ? `${i.name} (${i.categoryLabel})` : i.code ? `${i.code} - ${i.name}` : i.name);

		function extrasLines(extras) {
			if (!extras) return [];
			const lines = [];
			const tint = extra("tints", extras.tint);
			if (tint && tint.price) lines.push({ code: itemCode("TINT", tint.name, tint.id), itemName: `${tint.name} tint`, name: `${tint.name} tint ${extras.density}%${extras.gradient ? " gradient" : ""}`, price: tint.price + (extras.gradient ? EXTRAS.gradientPrice : 0) });
			const type = extra("lensTypes", extras.lensType);
			if (type && type.price) lines.push({ code: itemCode("LENSTYPE", type.name, type.id), itemName: type.name, name: type.name, price: type.price });
			(extras.coatings || []).forEach((id) => {
				const c = extra("coatings", id);
				if (c) lines.push({ code: itemCode("COAT", c.name, c.id), itemName: c.name, name: c.name + (c.included ? " (included)" : ""), price: c.included ? 0 : c.price });
			});
			const mirror = extra("mirrors", extras.mirror);
			if (mirror && mirror.price) lines.push({ code: itemCode("MIRROR", mirror.name, mirror.id), itemName: `${mirror.name} mirror`, name: `${mirror.name} mirror`, price: mirror.price });
			(extras.accessories || []).forEach((id) => {
				const a = extra("accessories", id);
				if (a) lines.push({ code: itemCode("ADDON", a.name, a.id), itemName: a.name, name: a.name, price: a.price, addOn: true });
			});
			return lines;
		}

		// ------------------------------------------------------------------
		// Contact lens helpers
		// ------------------------------------------------------------------
		const isContacts = (job) => !!job && job.type === "contacts";
		// A made-to-order hard lens: lab-made like glasses, billed per lens against its inventory SKU.
		const isCustom = (job) => isContacts(job) && !!job.product && !!job.product.custom;
		const homeDelivery = (job) => isContacts(job) && !!job.supply && job.supply.delivery === "home";
		const modalityMeta = (key) => CL_MODALITIES.find((m) => m.key === key) || CL_MODALITIES[0];
		const roundTo = (n, step) => Math.round(n / step) * step;

		// Stock contact lenses are made in 0.25 steps to ±6.00, then 0.50 steps: -6.00, -6.50, -7.00.
		const snapClPower = (f) => roundTo(f, Math.abs(f) > 6 ? 0.5 : 0.25);

		// The powers a stock lens is made in across its range, strongest plus first. Counted out from plano,
		// so past ±6.00 they land on the half dioptres.
		function clPowers(range) {
			const list = [];
			for (let q = Math.floor(range[1] * 4); q >= Math.ceil(range[0] * 4); q--) {
				if (Math.abs(q) <= 24 || q % 2 === 0) list.push(q / 4);
			}
			return list;
		}

		// The lens one eye wears: the left eye's own when the prescription gives it one (a toric right and a
		// spherical left, say), otherwise the prescription's lens. For a prescription or a job alike.
		const lensFor = (holder, eye) => (holder ? (eye === "os" && holder.productOs) || holder.product : null);
		// The eyes that wear a lens - one-eye wear leaves the other out.
		const lensEyes = (holder) => ["od", "os"].filter((k) => !(holder && holder.cl && holder.cl[k] && holder.cl[k].noLens));

		// An eye with `noLens` wears no contact lens.
		function emptyClRx() {
			const eye = () => ({ sph: null, cyl: null, axis: null, add: null, bc: null, dia: null, noLens: false });
			return { od: eye(), os: eye(), expires: null, prescriber: "", prescriberRegistration: null, source: null };
		}

		// A custom lens eye: the lab's usual starting point for the design, plus the diagnostic lens
		// the fitting was done with and the over-refraction through it.
		function customEye(product) {
			const d = product.defaults || {};
			return {
				sph: d.sph === undefined ? null : d.sph, cyl: null, axis: null, add: null, bc: d.bc, dia: d.dia, noLens: false,
				material: product.materials[0], colour: product.colours[0],
				trial: { bc: d.bc, sph: null, dia: d.dia }, or: null, orCyl: null, orAxis: null, bcSteep: null,
			};
		}

		// Order power for a hard lens: the diagnostic lens power plus what the patient still needed
		// over it, vertex-adjusted over 4D. Base curve and diameter carry over from the diagnostic lens.
		function orderPowerFromTrial(trialSph, overRefraction) {
			if (trialSph === null || trialSph === undefined || trialSph === "" || isNaN(trialSph)) return null;
			let or = Number(overRefraction) || 0;
			if (Math.abs(or) >= 4) or = or / (1 - 0.012 * or);
			return roundTo(Number(trialSph) + or, 0.25);
		}

		const colourHex = (name) => (CL_COLOURS.find((c) => c.name === name) || {}).hex || null;

		function emptyFitting() {
			return { fee: "initial", trialIssued: false, trialDate: null, outcome: null, va: { od: "", os: "" }, wear: "daily", followUp: null, notes: "", trials: [] };
		}

		// `lensPrice` and `leadDays` are a made-to-order lens's price per lens and the lab's lead time, set
		// when dispensing - the prescriber describes the lens, the dispenser prices it.
		function emptySupply() {
			return { months: 6, boxes: { od: 0, os: 0 }, care: [], delivery: "practice", autoReorder: true, lensPrice: null, leadDays: null };
		}

		// A custom lens's price per lens: the dispenser's for a made-to-order lens, else the catalogue's.
		const lensPriceFor = (job) => (job.supply && isNum(job.supply.lensPrice) ? Number(job.supply.lensPrice) : (job.product && job.product.price) || 0);

		// Boxes one eye needs to last `months`.
		function boxesFor(product, months) {
			if (!product) return 0;
			// One lens per eye; a spare is added by hand.
			if (product.custom) return 1;
			return Math.max(1, Math.ceil((months * 30) / (product.pack * modalityMeta(product.modality).days)));
		}

		// Days a supply actually lasts - the shorter eye, since the patient reorders when either runs out.
		// An eye supplied nothing (one-eye wear) doesn't count, and each eye lasts by its own lens's boxes.
		function supplyDays(job) {
			const p = job.product;
			if (!p || !job.supply) return 0;
			// A hard lens lasts until it is replaced, however many spares came with it.
			if (p.custom) return job.supply.boxes.od + job.supply.boxes.os > 0 ? (p.replaceMonths || 12) * 30 : 0;
			const days = ["od", "os"].filter((k) => job.supply.boxes[k] > 0).map(function (k) {
				const lens = lensFor(job, k);
				return job.supply.boxes[k] * lens.pack * modalityMeta(lens.modality).days;
			});
			return days.length ? Math.min.apply(null, days) : 0;
		}

		// When the supply runs out, counted from when the patient got it.
		function supplyEnds(job) {
			if (!isContacts(job) || !job.order || !job.order.collectedAt) return null;
			return addDays(job.order.collectedAt, supplyDays(job));
		}

		// Spectacle Rx -> contact lens power. Powers over 4D are vertex-compensated from a 12 mm back
		// vertex distance, a toric keeps the cylinder (snapped to the cylinders made, axis to 10°), and
		// anything else gets the spherical equivalent.
		// Spectacle Rx -> a starting point for a made-to-order hard lens, before (or instead of) a
		// diagnostic lens: the sphere of the minus-cylinder form, vertex-compensated over 4D - the tear lens
		// under a rigid lens corrects the corneal astigmatism, as when it is fitted on flat K. A toric design
		// keeps the cylinder (vertexed meridian by meridian, axis to the degree), a multifocal the add. The
		// base curve and diameter stay the fitter's.
		function toCustomRx(refraction, design) {
			const out = { sph: null, cyl: null, axis: null, add: null, vertexed: false };
			if (!refraction || !isNum(refraction.sph)) return out;
			const r = toMinusCyl(refraction);
			const vertex = (f) => {
				if (Math.abs(f) < 4) return f;
				out.vertexed = true;
				return f / (1 - 0.012 * f);
			};
			const sph = vertex(Number(r.sph));
			out.sph = roundTo(sph, 0.25);
			if (design === "toric" && isNum(r.cyl) && Number(r.cyl) !== 0) {
				out.cyl = roundTo(vertex(Number(r.sph) + Number(r.cyl)) - sph, 0.25) || null;
				out.axis = out.cyl && isNum(r.axis) ? Number(r.axis) : null;
			}
			if (design === "multifocal" && Number(refraction.add) > 0) out.add = roundTo(Number(refraction.add), 0.25);
			return out;
		}

		// A refraction written in plus cylinder is transposed first: contact lenses are made in minus.
		function toContactRx(refraction, product) {
			const out = { sph: null, cyl: null, axis: null, add: null, bc: product && product.bcs.length ? product.bcs[0] : null, dia: product ? product.dia : null, vertexed: false };
			if (!refraction || refraction.sph === null || refraction.sph === undefined || isNaN(refraction.sph)) return out;
			refraction = toMinusCyl(refraction);
			const vertex = (f) => {
				if (Math.abs(f) < 4) return f;
				out.vertexed = true;
				return f / (1 - 0.012 * f);
			};
			const sph = Number(refraction.sph);
			const cyl = Number(refraction.cyl) || 0;
			// A catalogue lens lists no cylinders or adds as none at all, not an empty list.
			const cyls = (product && product.cyls) || [];
			const adds = (product && product.adds) || [];
			if (product && product.design === "toric" && Math.abs(cyl) >= 0.75) {
				const m1 = vertex(sph);
				const m2 = vertex(sph + cyl);
				const want = m2 - m1;
				// The nearest cylinder made; on a tie the weaker one, since an under-corrected cylinder is
				// comfortable and an over-corrected one is not.
				out.cyl = cyls.length
					? cyls.reduce((best, c) => {
						const d = Math.abs(c - want) - Math.abs(best - want);
						return d < -0.001 || (Math.abs(d) <= 0.001 && c > best) ? c : best;
					})
					: Math.min(-0.25, roundTo(want, 0.25));
				out.sph = snapClPower(m1);
				out.axis = roundTo(Number(refraction.axis) || 180, 10) || 180;
			} else {
				out.sph = snapClPower(vertex(sph + cyl / 2));
			}
			if (product && product.design === "multifocal" && Number(refraction.add) > 0) {
				const add = Number(refraction.add);
				out.add = !adds.length ? roundTo(add, 0.25)
					: adds.length === 3 ? adds[add <= 1.25 ? 0 : add <= 2 ? 1 : 2]
						: adds.reduce((best, a) => (Math.abs(parseFloat(a) - add) < Math.abs(parseFloat(best) - add) ? a : best));
			}
			return out;
		}

		function contactsLines(job) {
			const lines = [];
			const p = job.product;
			const s = job.supply || emptySupply();
			if (p && p.custom) {
				const price = lensPriceFor(job);
				[["od", "R"], ["os", "L"]].forEach(function (eye) {
					const n = s.boxes[eye[0]] || 0;
					if (!n) return;
					lines.push({ code: itemCode("CL", p.name, p.id), itemName: p.name, name: `${p.name} - ${eye[1]} ${customSpec((job.cl && job.cl[eye[0]]) || {})}${n > 1 ? ` (${n} lenses)` : ""}`, price: n * price, cost: n * p.cost, kind: "Lenses", qty: n });
				});
				if (lines.length && p.warrantyDays) lines.push({ code: "CL-REMAKE-WARRANTY", itemName: "Lab remake warranty", name: `Lab remake warranty - ${p.warrantyDays} days`, price: 0, kind: "Warranty" });
			} else if (p) {
				const fit = (e) => (e.cyl ? "toric" : e.add ? e.add : "");
				// Each eye bills against its own lens, which can differ (a toric right and a spherical left).
				[["od", "R"], ["os", "L"]].forEach(function (eye) {
					const n = s.boxes[eye[0]] || 0;
					if (!n) return;
					const lens = lensFor(job, eye[0]);
					const e = (job.cl && job.cl[eye[0]]) || {};
					const item = `${lens.brand} ${lens.name} ${lens.pack}pk`;
					lines.push({ code: itemCode("CL", item, lens.id), itemName: item, name: `${item} - ${eye[1]} ${n} × ${fmtPower(e.sph)}${fit(e) ? " " + fit(e) : ""}`, price: n * lens.price, kind: "Lenses", qty: n });
				});
				if (s.months >= 12) {
					const lensTotal = lines.reduce((t, l) => t + l.price, 0);
					const discount = EXTRAS.annualSupplyDiscountPercent;
					// A saving has no item: it is posted to the sales account against the lenses.
					if (discount) lines.push({ code: null, name: `Annual supply saving (${discount}% off lenses)`, price: -Math.round(lensTotal * discount) / 100, kind: "Saving" });
				}
			}
			(s.care || []).forEach((id) => {
				const c = extra("careProducts", id);
				if (c) lines.push({ code: itemCode("CARE", c.name, c.id), itemName: c.name, name: c.name, price: c.price, kind: "Care" });
			});
			const fee = job.fitting && extra("fittingFees", job.fitting.fee);
			if (fee && fee.price) lines.push({ code: itemCode("FIT", fee.name, fee.id), itemName: fee.name, name: fee.name, price: fee.price, kind: "Fitting" });
			const shipping = delivery.find((d) => d.key === s.delivery);
			if (shipping && shipping.price) {
				const goods = lines.filter((l) => l.kind !== "Fitting").reduce((t, l) => t + l.price, 0);
				const free = shipping.freeOver !== null && goods >= shipping.freeOver;
				lines.push({ code: "CL-DELIVERY", itemName: "Delivery to patient", name: free ? `Delivery to patient (free over $${shipping.freeOver})` : "Delivery to patient", price: free ? 0 : shipping.price, kind: "Delivery" });
			}
			return lines;
		}

		// One custom lens's parameters, the way the lab writes them: BC / power / DIA, then cyl and add.
		// A made-to-order lens in one line, in the prescription table's order: powers, then the fit, then
		// its type and colour.
		function customSpec(e) {
			if (e.noLens) return "No lens";
			let s = fmtPower(e.sph);
			if (e.cyl) s += ` / ${fmtPower(e.cyl)} × ${e.axis || "?"}`;
			if (e.add) s += ` add ${fmtPower(e.add)}`;
			// A back-toric lens has a steep base curve too: "BC 7.80 / 7.40" (flat / steep).
			s += ` · BC ${e.bc ? Number(e.bc).toFixed(2) : "-"}${e.bcSteep ? " / " + Number(e.bcSteep).toFixed(2) : ""} · DIA ${e.dia ? Number(e.dia).toFixed(1) : "-"}`;
			if (e.material) s += ` · ${e.material}${e.colour && e.colour !== "Clear" ? " " + e.colour.toLowerCase() : ""}`;
			return s;
		}

		// A line under the job on the tile and job list: how much was ordered.
		function supplyText(job) {
			const b = (job.supply && job.supply.boxes) || { od: 0, os: 0 };
			if (isCustom(job)) return `Made to order · R ${b.od} / L ${b.os} lens${b.od + b.os === 1 ? "" : "es"}`;
			return `${job.supply.months}-month supply · R ${b.od} / L ${b.os} boxes`;
		}

		function fmtPower(v) {
			if (v === null || v === undefined || v === "" || isNaN(v)) return "-";
			const n = Number(v);
			return (n > 0 ? "+" : "") + n.toFixed(2);
		}

		// An eye's prisms as they are written, always to two decimals: "2.00Δ BI, 1.00Δ BU", or "" for none.
		const PRISM_BASES = { In: "BI", Out: "BO", Up: "BU", Down: "BD" };
		function fmtPrism(e) {
			if (!e) return "";
			return [[e.hPrism, e.hBase], [e.vPrism, e.vBase]]
				.filter((p) => Number(p[0]) > 0)
				.map((p) => `${Number(p[0]).toFixed(2)}Δ ${PRISM_BASES[p[1]] || ""}`.trim())
				.join(", ");
		}

		// An eye's monocular PD and fitting height: "PD 31.5 · height 20 mm", or "" when neither is measured.
		function fmtMeasurements(e) {
			if (!e) return "";
			const parts = [isNum(e.pd) ? `PD ${e.pd}` : null, isNum(e.height) ? `height ${e.height}` : null].filter(Boolean);
			return parts.length ? `${parts.join(" · ")} mm` : "";
		}

		// ------------------------------------------------------------------
		// What the lab makes
		// ------------------------------------------------------------------
		// The prescription is never changed; what the lab makes is worked out from it, for what the
		// glasses are for (job.use) and the lens design. A lens with zones (progressive, bifocal, office)
		// is made to the whole prescription and the lab places the ADD. A single vision lens has one power
		// per eye: the sphere for distance, sphere + ADD for reading, and for a screen the sphere plus the
		// intermediate ADD where one was prescribed, or else half the ADD (to the nearest 0.25 D).
		// Cylinder, axis and prism never change.
		const ZONED_DESIGNS = ["progressive", "bifocal", "office"];
		const USE_WORDS = { distance: "distance", near: "reading", intermediate: "computer", multifocal: "all distances" };
		const toQuarter = (v) => Math.round(v * 4) / 4;
		const addOf = (e) => Number(e && e.add) || 0;
		const intAddOf = (e) => Number(e && e.intAdd) || 0;
		const isNum = (v) => v !== null && v !== undefined && v !== "" && !isNaN(v);

		/**
		 * @param {Object} job    a glasses job: its rx (as prescribed, with the use it was written for) and use
		 * @param {String} design the lens design - the chosen lens's, or the one suggested for the use
		 * @returns {Object} { use, zoned, changed, short, rule, problem: { kind: "add"|"design", text } | null,
		 *                     eyes: { od, os } (what the lab makes), rows (prescribed and made, for display) }
		 */
		function lensPowers(job, design) {
			const rx = job.rx || {};
			const use = job.use || rx.use || "distance";
			const prescribed = rx.use || "distance";
			const hasAdd = addOf(rx.od) > 0 || addOf(rx.os) > 0;
			const hasIntAdd = intAddOf(rx.od) > 0 || intAddOf(rx.os) > 0;
			const zoned = ZONED_DESIGNS.indexOf(design) !== -1;
			const designLabel = ((LENS_DESIGNS.find((d) => d.key === design) || {}).label || "").toLowerCase();
			const out = { use: use, zoned: zoned, changed: false, short: "", rule: "", problem: null, eyes: null, rows: [] };
			const make = (sphFor, keepAdd) => {
				const eye = (e) => angular.extend({}, e, {
					sph: e && isNum(e.sph) ? toQuarter(Number(e.sph) + sphFor(e)) : e && e.sph,
					add: keepAdd ? e && e.add : null,
					intAdd: keepAdd ? e && e.intAdd : null,
				});
				return { od: eye(rx.od || {}), os: eye(rx.os || {}) };
			};

			if (zoned) {
				if (!hasAdd) {
					out.short = "Needs an ADD";
					const article = /^[aeiou]/.test(designLabel) ? "An" : "A";
					out.problem = { kind: "design", text: `${article} ${designLabel} lens needs an ADD, and this prescription has none. Choose a single vision lens, or ask the prescriber for a prescription with an ADD.` };
					return out;
				}
				out.eyes = make(() => 0, true);
				out.short = "Full Rx with ADD";
				out.rule = `${/^[aeiou]/.test(designLabel) ? "An" : "A"} ${designLabel} lens is made to the full prescription - the lab places the ADD.`;
			} else if (use === "multifocal") {
				out.short = "Full Rx with ADD";
				out.problem = { kind: "design", text: "All distances needs a progressive, bifocal or office lens - a single vision lens has one power." };
				return out;
			} else if (use === "intermediate" && hasIntAdd) {
				out.eyes = make((e) => intAddOf(e), false);
				out.short = "Sphere + int. ADD";
				out.rule = "Computer power is the sphere plus the intermediate ADD prescribed. Cylinder and axis are as prescribed.";
			} else if (!hasAdd) {
				// Without an ADD the sphere is the power for the use it was prescribed for, and only that.
				if (use !== prescribed && !(prescribed === "multifocal" && use === "distance")) {
					out.short = "Needs an ADD";
					out.problem = {
						kind: "add",
						text: `This prescription is for ${USE_WORDS[prescribed]} and has no ADD, so a ${USE_WORDS[use]} power can't be worked out from it. Keep ${USE_WORDS[prescribed]}, or ask the prescriber for a ${USE_WORDS[use]} prescription.`,
					};
					return out;
				}
				out.eyes = make(() => 0, false);
				out.short = "As prescribed";
				out.rule = "Made exactly as prescribed.";
			} else if (use === "near") {
				out.eyes = make((e) => addOf(e), false);
				out.short = "Sphere + ADD";
				out.rule = "Reading power is the sphere plus the ADD. Cylinder and axis are as prescribed.";
			} else if (use === "intermediate") {
				out.eyes = make((e) => toQuarter(addOf(e) / 2), false);
				out.short = "Sphere + ½ ADD";
				out.rule = "Computer power is the sphere plus half the ADD, to the nearest 0.25 D - check the working distance with the prescriber. Cylinder and axis are as prescribed.";
			} else {
				out.eyes = make(() => 0, false);
				out.short = "ADD not used";
				out.rule = "Distance power is the sphere as prescribed - the ADD isn't used.";
			}

			const fmtAxis = (v) => (isNum(v) ? String(v) : "-");
			const cells = (e) => [fmtPower(e.sph), fmtPower(e.cyl), fmtAxis(e.axis), fmtPower(e.add)];
			[["od", "Right"], ["os", "Left"]].forEach(function (k) {
				const before = cells(rx[k[0]] || {});
				const after = cells(out.eyes[k[0]]);
				out.rows.push({ eye: k[1], kind: "Prescribed", cells: before.map((v) => ({ value: v, changed: false })) });
				out.rows.push({ eye: k[1], kind: "Lab makes", made: true, cells: after.map((v, i) => ({ value: v, changed: v !== before[i] })) });
			});
			out.changed = out.rows.some((r) => r.cells.some((c) => c.changed));
			return out;
		}

		// An ADD as the vision module takes a refraction's: 0 to +6.00, in 0.25 steps. A named contact lens
		// add (LOW, MED, HIGH, "MED D") is the lens's own, and a power can carry a multifocal's dominant or
		// non-dominant lens ("+2.00 D", "+2.00 N"). Returns what is wrong, or null.
		const ADD_LIMITS = { min: 0, max: 6, step: 0.25 };
		function addProblem(value) {
			if (value === null || value === undefined || value === "") return null;
			const s = String(value).trim().replace(/^\+/, "").replace(/^(-?\d*\.?\d+)\s*[DN]$/i, "$1");
			if (!/^-?\d*\.?\d+$/.test(s)) return /^[A-Za-z]+(\s[A-Za-z]+)?$/.test(s) ? null : "The ADD isn't a number.";
			const n = Number(s);
			if (n < ADD_LIMITS.min || n > ADD_LIMITS.max) return "The ADD must be from 0 to +6.00.";
			if (Math.abs(n * 4 - Math.round(n * 4)) > 1e-9) return "The ADD goes in 0.25 steps.";
			return null;
		}

		function applyBilling(subtotal, billing) {
			billing = billing || {};
			let discount = Number(billing.discount) || 0;
			if (billing.discountType === "percent") discount = Math.round(subtotal * discount) / 100;
			discount = Math.min(discount, subtotal);
			const total = subtotal - discount;
			const gst = Math.round((total * gstRate) / (1 + gstRate) * 100) / 100;
			const deposit = billing.paymentOption === "deposit" ? Math.round(total * 50) / 100 : billing.paymentOption === "full" ? total : 0;
			return { subtotal, discount, total, gst, deposit, balance: total - deposit };
		}

		// A glasses job's priced lines: frame, lenses and extras.
		function glassesLines(job) {
			const lines = [];
			// One Xero item per frame model (its colours share it) and per catalogue lens.
			if (job.frame && job.frame.own) {
				if (Number(job.frame.price) > 0) {
					const name = "Fitting lenses to patient's own frame";
					lines.push({ code: itemCode("FRAME", "Own frame fitting", OWN_FRAME_ID), itemName: name, name: name, price: job.frame.price, kind: "Frame" });
				}
			} else if (job.frame && job.frame.manual) {
				// Its name stands in for an id, so the same frame entered again is the same Xero item.
				lines.push({ code: itemCode("FRAME", frameName(job.frame), `${OTHER_FRAME_ID}:${frameName(job.frame).toLowerCase()}`), itemName: frameName(job.frame), name: [frameName(job.frame), job.frame.colour.name].filter(Boolean).join(" - "), price: job.frame.price, kind: "Frame" });
			} else if (job.frame) {
				const frameItem = (job.frame.inventory && job.frame.inventory.itemId) || job.frame.id;
				lines.push({ code: itemCode("FRAME", frameName(job.frame), frameItem), itemName: frameName(job.frame), name: `${frameName(job.frame)} - ${job.frame.colour.name}`, price: job.frame.price, kind: "Frame" });
			}
			if (job.lens) {
				const lensName = `${job.lens.supplier} ${job.lens.name}${job.lens.name.indexOf(String(job.lens.index)) === -1 ? " " + job.lens.index : ""}`;
				lines.push({ code: itemCode("LENS", `${job.lens.supplier} ${job.lens.name}`, job.lens.id), itemName: lensName, name: `${lensName} (pair)`, price: job.lens.price, kind: "Lens" });
			}
			extrasLines(job.extras).forEach((l) => lines.push(angular.extend({ kind: "Extra" }, l)));
			return lines;
		}

		// Items added by hand - an eye examination, say - so one invoice covers the visit and the
		// glasses, instead of a second invoice for the consult. Named and priced on the job. One picked
		// from the billing item search keeps that item's code, so Xero bills the existing item.
		function customLines(job) {
			return (job.customItems || [])
				.filter((i) => i && (i.name || "").trim())
				.map((i) => ({
					code: i.code || itemCode("SVC", i.name, (i.name || "").trim().toLowerCase()),
					itemName: (i.name || "").trim(),
					name: (i.name || "").trim(),
					price: Number(i.price) || 0,
					kind: "Service",
				}));
		}

		// The job's priced lines: as invoiced once it has been, otherwise from today's prices.
		function jobLines(job) {
			if (job.invoice && job.invoice.lines && job.invoice.lines.length) return job.invoice.lines;
			return (isContacts(job) ? contactsLines(job) : glassesLines(job)).concat(customLines(job));
		}

		function totals(job) {
			const lines = jobLines(job);
			const sum = (kinds) => lines.filter((l) => kinds.indexOf(l.kind) !== -1).reduce((t, l) => t + Number(l.price), 0);
			const subtotal = lines.reduce((t, l) => t + Number(l.price), 0);
			if (isContacts(job)) {
				// What the lab charges for custom lenses, against what they are sold for - practice eyes only.
				const cost = contactsLines(job).reduce((t, l) => t + (l.cost || 0), 0);
				return angular.extend(
					{ lensPrice: sum(["Lenses", "Saving"]), carePrice: sum(["Care"]), fittingPrice: sum(["Fitting"]), deliveryPrice: sum(["Delivery"]), labCost: cost, lensMargin: cost ? sum(["Lenses"]) - cost : 0 },
					applyBilling(subtotal, job.billing)
				);
			}
			return angular.extend({ framePrice: sum(["Frame"]), lensPrice: sum(["Lens"]), extrasPrice: sum(["Extra"]) }, applyBilling(subtotal, job.billing));
		}

		function statusMeta(key) {
			return STATUSES.find((s) => s.key === key) || STATUSES[0];
		}

		// Where a job is up to, worded for its pathway ("Dispatched" rather than "At lab" for contacts).
		function statusFor(job) {
			const labels = isCustom(job) ? {} : homeDelivery(job) ? CL_HOME_LABELS : isContacts(job) ? CL_LABELS : {};
			return angular.extend({}, statusMeta(job.status), labels[job.status] || {});
		}
		const statusLabel = (job) => statusFor(job).label;
		const statusIcon = (job) => statusFor(job).icon;

		const trackFor = (job) => (isCustom(job) ? TRACKS.glasses : homeDelivery(job) ? TRACKS.contactsHome : isContacts(job) ? TRACKS.contacts : TRACKS.glasses);
		const trackIndex = (job) => trackFor(job).findIndex((s) => s.key === job.status);

		// Who the order goes to: the edging lab for glasses, the distributor for stock contacts, the
		// contact lens lab for custom ones.
		function supplierFor(job) {
			if (isContacts(job)) {
				if (!job.product) return "";
				return CL_SUPPLIERS[job.product.maker] || (job.product.custom ? job.product.maker || "The lab" : `${job.product.maker} distribution`);
			}
			return job.lens ? LABS[job.lens.supplier] || `${job.lens.supplier} lab` : "";
		}


		const leadDaysFor = (job) =>
			isContacts(job) ? (job.supply && isNum(job.supply.leadDays) ? Number(job.supply.leadDays) : job.product ? job.product.leadDays : 3) : job.lens ? job.lens.leadDays : 5;

		function statusIndex(key) {
			return STATUSES.findIndex((s) => s.key === key);
		}

		// Invoiced: the job is final and nothing on it changes. Only a quote, or a new job, is edited.
		const isLocked = (job) => !!job && statusIndex(job.status) >= statusIndex("INVOICED");


		function addDays(date, n) {
			const d = new Date(date);
			d.setDate(d.getDate() + n);
			return d;
		}

		// The date that matters for where the job is up to.
		function keyDate(job) {
			const o = job.order || {};
			switch (job.status) {
				case "QUOTE": return { label: "Quoted", date: job.created };
				case "INVOICED": return { label: "Invoiced", date: job.invoice && job.invoice.created };
				case "ORDERED":
				case "IN_PRODUCTION": return o.eta ? { label: "Due", date: o.eta } : { label: "Ordered", date: o.sentAt };
				case "RECEIVED": return { label: "Arrived", date: o.receivedAt };
				case "COLLECTED": return { label: homeDelivery(job) ? "Delivered" : "Collected", date: o.collectedAt };
			}
			return { label: "Created", date: job.created };
		}

		// At the lab past its expected date.
		function isOverdue(job) {
			const o = job.order || {};
			return (job.status === "ORDERED" || job.status === "IN_PRODUCTION") && !!o.eta && new Date(o.eta) < new Date();
		}

		// ------------------------------------------------------------------
		// Lens catalogue (the practice's, from the API)
		// ------------------------------------------------------------------
		const catalogue = { lenses: [], contactLenses: [], loaded: false };
		let cataloguePromise = null;
		let cataloguePracticeId = null;

		const num = (v) => (v === null || v === undefined || v === "" || isNaN(v) ? null : Number(v));
		const nonEmpty = (list) => (list && list.length ? list : undefined);

		function catalogueLensFromApi(i) {
			return {
				id: i.lens_id, supplier: i.brand, name: i.name, design: i.design, index: num(i.lens_index), material: i.material,
				price: num(i.price) || 0, leadDays: i.lead_days || 0, features: i.features || [],
			};
		}

		function catalogueContactLensFromApi(i) {
			const p = {
				id: i.lens_id, brand: i.brand, name: i.name, maker: i.maker, modality: i.modality, design: i.design, family: i.family,
				material: i.material, custom: !!i.custom, pack: i.pack_size || 1, price: num(i.price) || 0, cost: num(i.cost),
				leadDays: i.lead_days || 0, warrantyDays: i.warranty_days, replaceMonths: i.replace_months, features: i.features || [],
				bcs: (i.base_curves || []).map(Number), dia: num(i.diameter), range: [num(i.power_min), num(i.power_max)],
				cyls: nonEmpty((i.cylinders || []).map(Number)), adds: nonEmpty(i.adds),
			};
			if (p.custom) {
				angular.extend(p, {
					materials: i.materials || [],
					colours: nonEmpty(i.colours) || ["Clear"],
					bcRange: [num(i.base_curve_min), num(i.base_curve_max)],
					diaRange: [num(i.diameter_min), num(i.diameter_max)],
					defaults: { bc: num(i.default_base_curve), dia: num(i.default_diameter), sph: num(i.default_sphere) === null ? undefined : num(i.default_sphere) },
				});
			}
			return p;
		}

		// The practice's catalogue, read once per practice. Resolves to { lenses, contactLenses }.
		function loadCatalogue() {
			const practiceId = Session.practice && Session.practice.id;
			if (cataloguePromise && cataloguePracticeId === practiceId) return cataloguePromise;
			cataloguePracticeId = practiceId;
			cataloguePromise = LensCatalogueAPI.getItems().then(
				function (resp) {
					const items = resp.data || [];
					catalogue.lenses = items.filter((i) => i.pathway === "GLASSES").map(catalogueLensFromApi);
					catalogue.contactLenses = items.filter((i) => i.pathway === "CONTACTS").map(catalogueContactLensFromApi);
					catalogue.loaded = true;
					return catalogue;
				},
				function (resp) {
					cataloguePromise = null;
					return $q.reject(resp);
				}
			);
			return cataloguePromise;
		}

		const contactLensById = (id) => catalogue.contactLenses.find((c) => c.id === id) || clStock.items.find((c) => c.id === id) || null;

		// ------------------------------------------------------------------
		// Dates
		// ------------------------------------------------------------------
		// A yyyy-MM-dd date as local midnight, so date inputs and comparisons see the day it names.
		function parseDate(value) {
			if (!value) return null;
			const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
			return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
		}
		const parseDateTime = (value) => (value ? new Date(value) : null);
		const fmtDate = (date) => (date ? moment(date).format("YYYY-MM-DD") : null);

		// ------------------------------------------------------------------
		// Rx, as the API sends it and as the dialogs edit it
		// ------------------------------------------------------------------
		function spectacleEyeFromApi(e) {
			e = e || {};
			const eye = {
				sph: num(e.sphere), cyl: num(e.cylinder), axis: num(e.axis), intAdd: num(e.intermediate_add), add: num(e.add),
				hPrism: num(e.horizontal_prism), hBase: e.horizontal_prism_base || "",
				vPrism: num(e.vertical_prism), vBase: e.vertical_prism_base || "",
				pd: num(e.pupillary_distance), height: num(e.fitting_height), balance: !!e.balance,
			};
			// A prescription from before horizontal and vertical prisms were kept apart has a single prism;
			// its base says which it is.
			if (num(e.prism) && eye.hPrism === null && eye.vPrism === null) {
				const vertical = e.prism_base === "Up" || e.prism_base === "Down";
				eye[vertical ? "vPrism" : "hPrism"] = num(e.prism);
				eye[vertical ? "vBase" : "hBase"] = e.prism_base || "";
			}
			return eye;
		}

		function spectacleEyeToApi(e) {
			e = e || {};
			return {
				sphere: num(e.sph), cylinder: num(e.cyl), axis: num(e.axis), intermediate_add: num(e.intAdd), add: num(e.add),
				horizontal_prism: num(e.hPrism), horizontal_prism_base: num(e.hPrism) ? e.hBase || null : null,
				vertical_prism: num(e.vPrism), vertical_prism_base: num(e.vPrism) ? e.vBase || null : null,
				pupillary_distance: num(e.pd), fitting_height: num(e.height), balance: !!e.balance,
			};
		}

		function spectacleRxFromApi(r) {
			r = r || {};
			return {
				od: spectacleEyeFromApi(r.right_eye), os: spectacleEyeFromApi(r.left_eye), pd: num(r.pupillary_distance), use: r.use || "distance",
				expires: parseDate(r.expiry_date), prescriber: r.prescriber_name || "", prescriberRegistration: r.prescriber_registration || null,
				source: r.source || null,
			};
		}

		// The prescriber's registration is set by the server when the prescription is issued, so it isn't sent.
		function spectacleRxToApi(rx) {
			return {
				right_eye: spectacleEyeToApi(rx.od), left_eye: spectacleEyeToApi(rx.os), pupillary_distance: num(rx.pd), use: rx.use,
				prescriber_name: rx.prescriber || null, expiry_date: fmtDate(rx.expires), source: rx.source || null,
			};
		}

		// A prescription's lens, as prescribed, with the parameters it is made in from the catalogue.
		function productFromApi(s) {
			if (!s) return null;
			const fromCatalogue = s.product_id === MADE_TO_ORDER_ID ? madeToOrderLens() : contactLensById(s.product_id) || { bcs: [], range: [-20, 20] };
			return angular.extend(angular.copy(fromCatalogue), {
				id: s.product_id, inventoryItemId: s.inventory_item_id, fromInventory: !!s.inventory_item_id && !s.custom,
				brand: s.brand, name: s.name, maker: s.maker, modality: s.modality,
				design: s.design, family: s.family, material: s.material, custom: !!s.custom, pack: s.pack_size || 1,
				price: num(s.price) || 0, cost: num(s.cost), leadDays: s.lead_days || 0, warrantyDays: s.warranty_days, replaceMonths: s.replace_months,
			});
		}

		function productToApi(p) {
			if (!p) return null;
			return {
				product_id: p.id, inventory_item_id: p.inventoryItemId || null, brand: p.brand, name: p.name, maker: p.maker, modality: p.modality,
				design: p.design, family: p.family || null, material: p.material || (p.materials ? p.materials.join(", ") : null), custom: !!p.custom,
				pack_size: p.pack, price: p.price, cost: num(p.cost), lead_days: p.leadDays, warranty_days: p.warrantyDays || null,
				replace_months: p.replaceMonths || null,
			};
		}

		function contactEyeFromApi(e, custom) {
			e = e || {};
			const eye = {
				sph: num(e.sphere), cyl: num(e.cylinder), axis: num(e.axis), add: custom ? num(e.add) : e.add || null, bc: num(e.base_curve), dia: num(e.diameter),
				noLens: !!e.no_lens,
			};
			if (custom) {
				angular.extend(eye, {
					material: e.material || null, colour: e.colour || null,
					trial: { bc: num(e.trial_base_curve), sph: num(e.trial_sphere), dia: num(e.trial_diameter) }, or: num(e.over_refraction),
					orCyl: num(e.over_refraction_cylinder), orAxis: num(e.over_refraction_axis), bcSteep: num(e.base_curve_steep),
				});
			}
			return eye;
		}

		function contactEyeToApi(e) {
			e = e || {};
			const trial = e.trial || {};
			return {
				sphere: num(e.sph), cylinder: num(e.cyl), axis: num(e.axis), add: e.add === null || e.add === undefined || e.add === "" ? null : String(e.add),
				base_curve: num(e.bc), diameter: num(e.dia), material: e.material || null, colour: e.colour || null,
				trial_base_curve: num(trial.bc), trial_sphere: num(trial.sph), trial_diameter: num(trial.dia), over_refraction: num(e.or),
				over_refraction_cylinder: num(e.orCyl), over_refraction_axis: num(e.orAxis), base_curve_steep: num(e.bcSteep),
				no_lens: !!e.noLens,
			};
		}

		function contactLensRxFromApi(r, custom) {
			r = r || {};
			return {
				od: contactEyeFromApi(r.right_eye, custom), os: contactEyeFromApi(r.left_eye, custom), expires: parseDate(r.expiry_date),
				prescriber: r.prescriber_name || "", prescriberRegistration: r.prescriber_registration || null, source: r.source || null,
				specNotes: r.spec_notes || "",
			};
		}

		// `productOs` is the left eye's lens, when it isn't the right eye's.
		function contactLensRxToApi(cl, product, productOs) {
			return {
				product: productToApi(product), left_product: productToApi(productOs || null), right_eye: contactEyeToApi(cl.od), left_eye: contactEyeToApi(cl.os),
				prescriber_name: cl.prescriber || null, expiry_date: fmtDate(cl.expires), source: cl.source || null, spec_notes: cl.specNotes || null,
			};
		}

		// A contact lens trial, kept with its prescription draft, oldest first: the lens and powers each eye
		// took home, when, when it's due back, and what the review found - over-refraction, VA, the fit
		// and comfort - and how it came out. A new trial pair is another trial; none is ever overwritten.
		function trialFromApi(t) {
			const eye = (e) => (e ? { sph: num(e.sphere), cyl: num(e.cylinder), axis: num(e.axis), add: e.add || null, bc: num(e.base_curve), dia: num(e.diameter), noLens: !!e.no_lens } : null);
			const review = (r) => ({
				orSph: num((r || {}).over_refraction_sphere), orCyl: num((r || {}).over_refraction_cylinder), orAxis: num((r || {}).over_refraction_axis),
				va: (r || {}).visual_acuity || "", centration: (r || {}).centration || null, movement: (r || {}).movement || null,
				rotation: num((r || {}).rotation_degrees), rotationDir: (r || {}).rotation_direction || null, comfort: (r || {}).comfort || null,
			});
			const lens = (l) => (l ? { id: l.product_id || null, name: l.name || "" } : null);
			return {
				sent: parseDate(t.sent_date), review: parseDate(t.review_date),
				lens: { od: lens(t.right_lens), os: lens(t.left_lens) }, rx: { od: eye(t.right_eye), os: eye(t.left_eye) },
				findings: { od: review(t.right_review), os: review(t.left_review) }, notes: t.notes || "", outcome: t.outcome || null,
			};
		}
		function trialToApi(t) {
			const eye = (e) => (e ? { sphere: num(e.sph), cylinder: num(e.cyl) || null, axis: num(e.cyl) ? num(e.axis) : null, add: e.add ? String(e.add) : null, base_curve: num(e.bc), diameter: num(e.dia), no_lens: !!e.noLens } : null);
			const review = (r) => (r ? {
				over_refraction_sphere: num(r.orSph), over_refraction_cylinder: num(r.orCyl) || null, over_refraction_axis: num(r.orCyl) ? num(r.orAxis) : null,
				visual_acuity: String(r.va || "").trim() || null, centration: r.centration || null, movement: r.movement || null,
				rotation_degrees: num(r.rotation), rotation_direction: num(r.rotation) ? r.rotationDir || null : null, comfort: r.comfort || null,
			} : null);
			const lens = (l) => (l ? { product_id: l.id || null, name: l.name || null } : null);
			const out = { sent_date: fmtDate(t.sent), review_date: fmtDate(t.review), notes: String(t.notes || "").trim() || null, outcome: t.outcome || null };
			["od", "os"].forEach(function (k) {
				const side = k === "od" ? "right" : "left";
				out[`${side}_lens`] = lens(t.lens[k]);
				out[`${side}_eye`] = eye(t.rx[k]);
				out[`${side}_review`] = review(t.findings[k]);
			});
			return out;
		}

		// The trial fields the prescription is checked by - whether it went home on trial, when, how it came
		// out, the VA with it - are the latest trial's, kept in step with the list.
		function syncTrial(f) {
			const last = f.trials && f.trials.length ? f.trials[f.trials.length - 1] : null;
			return angular.extend(f, last
				? { trialIssued: true, trialDate: last.sent, outcome: last.outcome || null, va: { od: last.findings.od.va || "", os: last.findings.os.va || "" } }
				: { trialIssued: false, trialDate: null, outcome: null, va: { od: "", os: "" } });
		}

		function fittingFromApi(f, fee) {
			f = f || {};
			const fitting = {
				fee: fee || "none", trialIssued: !!f.trial_issued, trialDate: parseDate(f.trial_date), outcome: f.outcome || null,
				va: { od: f.visual_acuity_right || "", os: f.visual_acuity_left || "" }, wear: f.wear_schedule || "daily",
				followUp: parseDate(f.follow_up_date), notes: f.notes || "", trials: (f.trials || []).map(trialFromApi),
			};
			// A draft from before trials were kept went home on one trial, its review date in the follow-up.
			if (!fitting.trials.length && fitting.trialIssued) {
				const findings = (va) => angular.extend(trialFromApi({}).findings.od, { va: va });
				fitting.trials = [{
					sent: fitting.trialDate, review: fitting.followUp, lens: { od: null, os: null }, rx: { od: null, os: null },
					findings: { od: findings(fitting.va.od), os: findings(fitting.va.os) }, notes: "", outcome: fitting.outcome,
				}];
				fitting.followUp = null;
			}
			return syncTrial(fitting);
		}

		function fittingToApi(f) {
			f = syncTrial(angular.copy(f || {}));
			const va = f.va || {};
			return {
				trial_issued: !!f.trialIssued, trial_date: fmtDate(f.trialDate), outcome: f.outcome || null,
				visual_acuity_right: va.od || null, visual_acuity_left: va.os || null, wear_schedule: f.wear || null,
				follow_up_date: fmtDate(f.followUp), notes: f.notes || null, trials: (f.trials || []).map(trialToApi),
			};
		}

		// ------------------------------------------------------------------
		// Jobs, as the API sends them and as the dialogs edit them
		// ------------------------------------------------------------------
		// What a job is called on screen: what it is and the day it was started. Ids stay internal.
		const kindLabel = (job) => (isCustom(job) ? "Hard lenses" : isContacts(job) ? "Contact lenses" : "Glasses");
		const jobTitle = (job) => (job.id && job.created ? `${kindLabel(job)} · ${moment(job.created).format("D MMM YYYY")}` : kindLabel(job));

		function frameFromApi(f) {
			if (!f) return null;
			if (f.patient_owned) {
				return angular.extend(ownFrame(), {
					model: f.name || "", colour: { name: f.colour || "", hex: frameColourHex(f.colour) }, size: f.size || "",
					material: f.material || "", shape: f.shape || "rectangle", price: num(f.price) || 0,
				});
			}
			if (!f.inventory_item_id) {
				return angular.extend(otherFrame(), {
					model: f.name || "", colour: { name: f.colour || "", hex: frameColourHex(f.colour) }, size: f.size || "",
					material: f.material || "", shape: f.shape || "rectangle", price: num(f.price) || 0,
				});
			}
			const colour = { name: f.colour || "Standard", hex: frameColourHex(f.colour), variationId: f.inventory_variation_id, size: f.size || "", barcode: f.sku || "" };
			return {
				id: f.inventory_item_id, brand: "", model: f.name, shape: f.shape || "rectangle", material: f.material || "", price: num(f.price) || 0,
				fromInventory: true, colours: [colour], colour: colour, size: f.size || "", sku: f.sku,
				inventory: { itemId: f.inventory_item_id, variationId: f.inventory_variation_id }, stockDeducted: !!f.stock_deducted,
			};
		}

		function frameToApi(fr) {
			if (!fr) return null;
			if (fr.own || fr.manual) {
				return {
					patient_owned: !!fr.own, name: fr.model || null, colour: (fr.colour && fr.colour.name) || null, size: fr.size || null,
					material: fr.material || null, shape: fr.shape || null, price: num(fr.price) || 0,
				};
			}
			const inventory = fr.inventory || {};
			return {
				inventory_item_id: inventory.itemId || fr.id, inventory_variation_id: inventory.variationId || null, sku: fr.sku || null,
				name: frameName(fr), colour: fr.colour ? fr.colour.name : null, size: fr.size || null, material: fr.material || null,
				shape: fr.shape || null, price: fr.price,
			};
		}

		function jobLensFromApi(l) {
			if (!l) return null;
			const fromCatalogue = catalogue.lenses.find((c) => c.id === l.lens_id);
			return {
				id: l.lens_id, supplier: l.supplier, name: l.name, design: l.design, index: num(l.lens_index), material: l.material,
				price: num(l.price) || 0, leadDays: l.lead_days || 0, features: fromCatalogue ? fromCatalogue.features : [],
			};
		}

		const jobLensToApi = (l) => (l ? { lens_id: l.id, supplier: l.supplier, name: l.name, design: l.design, lens_index: l.index, material: l.material, price: l.price, lead_days: l.leadDays } : null);

		function extrasFromApi(x) {
			if (!x) return emptyExtras();
			return {
				tint: x.tint || "clear", density: x.tint_density || 50, gradient: !!x.gradient, lensType: x.lens_type || "standard",
				coatings: x.coatings || [], mirror: x.mirror || "none", accessories: x.accessories || [],
			};
		}

		const extrasToApi = (x) => (x ? { tint: x.tint, tint_density: x.density, gradient: !!x.gradient, lens_type: x.lensType, coatings: x.coatings, mirror: x.mirror, accessories: x.accessories } : null);

		function traceFromApi(t) {
			if (!t) return null;
			return {
				a: num(t.box_a), b: num(t.box_b), dbl: num(t.distance_between_lenses), ed: num(t.effective_diameter), circumference: num(t.circumference),
				frameCurve: num(t.frame_curve), device: t.device || null, mode: t.mode || null, format: t.format || null, tracedAt: parseDateTime(t.date_traced),
				// The tracer's export file, when the trace was imported rather than typed in.
				outline: t.outline || null,
			};
		}

		function traceToApi(t) {
			if (!t) return null;
			return {
				box_a: num(t.a), box_b: num(t.b), distance_between_lenses: num(t.dbl), effective_diameter: num(t.ed), circumference: num(t.circumference),
				frame_curve: num(t.frameCurve), device: t.device || null, mode: t.mode || null, format: t.format || null, outline: t.outline || null,
				date_traced: t.tracedAt ? moment(t.tracedAt).format("YYYY-MM-DDTHH:mm:ss") : null,
			};
		}

		function supplyFromApi(s) {
			if (!s) return emptySupply();
			return {
				months: s.months || 6, boxes: { od: s.boxes_right || 0, os: s.boxes_left || 0 }, care: s.care || [], delivery: s.delivery || "practice", autoReorder: s.auto_reorder !== false,
				lensPrice: num(s.lens_price), leadDays: num(s.lead_days),
			};
		}

		function supplyToApi(s, job) {
			if (!s) return null;
			const variation = (eye) => {
				const v = job ? stockFor(lensFor(job, eye), job.cl && job.cl[eye]) : null;
				return v ? v.variationId : null;
			};
			return {
				months: s.months, boxes_right: s.boxes.od, boxes_left: s.boxes.os, care: s.care, delivery: s.delivery, auto_reorder: !!s.autoReorder,
				variation_right: variation("od"), variation_left: variation("os"), lens_price: num(s.lensPrice), lead_days: num(s.leadDays),
			};
		}

		function jobFromApi(r) {
			const contacts = r.pathway === "CONTACTS";
			const product = contacts ? productFromApi(r.contact_lens_rx && r.contact_lens_rx.product) : null;
			const order = r.order || {};
			const job = {
				id: r.job_id,
				patientId: r.patient_id,
				created: parseDateTime(r.date_created),
				status: r.status,
				rxId: r.prescription_id,
				billing: r.billing
					? { discount: num(r.billing.discount) || 0, discountType: r.billing.discount_type || "amount", paymentOption: r.billing.payment_option || "full" }
					: { discount: 0, discountType: "amount", paymentOption: contacts ? "full" : "deposit" },
				invoice: r.invoice
					? {
						number: r.invoice.invoice_number || null, total: num(r.invoice.total), dueNow: num(r.invoice.amount_due_now),
						sentTo: r.invoice.sent_to, created: parseDateTime(r.invoice.date_invoiced), sentAt: parseDateTime(r.invoice.date_invoiced),
						xeroId: r.invoice.xero_invoice_id || null, xeroStatus: r.invoice.xero_status || null,
						paid: num(r.invoice.amount_paid) || 0,
						payments: (r.invoice.payments || []).map((p) => ({ amount: num(p.amount) || 0, method: p.method, date: parseDate(p.date_paid) })),
						// The discount is carried by the job's billing, so the invoice's own line is left out here.
						lines: (r.invoice.lines || []).filter((l) => l.kind !== "Discount")
							.map((l) => ({ kind: l.kind, code: l.code, itemName: l.item_name, name: l.name, price: num(l.price) || 0, qty: num(l.quantity) })),
					}
					: null,
				order: {
					notes: order.notes || "", lab: order.lab_name || null, ref: order.reference || null, sentAt: parseDateTime(order.date_sent),
					acceptedAt: parseDateTime(order.date_accepted), eta: parseDate(order.date_due), receivedAt: parseDateTime(order.date_received),
					collectedAt: parseDateTime(order.date_collected), documentId: order.document_id || null, reorderOf: order.reorder_of_job_id || null,
					// The order PDF the server made and filed; documentId is a PDF an older job uploaded itself.
					generatedDocumentId: order.generated_document_id || null,
				},
				notes: (r.notes || []).map((n) => ({ text: n.text, by: n.created_by_user_name || "", at: parseDateTime(n.date_created) })),
				// Items added by hand to go on the invoice with the job (an eye examination, say).
				customItems: (r.custom_items || []).map((i) => ({ name: i.name || "", price: num(i.price) || 0, code: i.code || null })),
			};
			if (contacts) {
				return angular.extend(job, {
					type: "contacts",
					product: product,
					productOs: productFromApi(r.contact_lens_rx && r.contact_lens_rx.left_product),
					cl: contactLensRxFromApi(r.contact_lens_rx, !!(product && product.custom)),
					fitting: fittingFromApi(r.fitting, r.fitting_fee),
					supply: supplyFromApi(r.supply),
				});
			}
			return angular.extend(job, {
				rx: spectacleRxFromApi(r.spectacle_rx),
				// What this pair is for; the rx keeps the use it was prescribed for.
				use: r.glasses_use || (r.spectacle_rx && r.spectacle_rx.use) || "distance",
				frame: frameFromApi(r.frame),
				lens: jobLensFromApi(r.lens),
				extras: extrasFromApi(r.extras),
				trace: traceFromApi(r.trace),
			});
		}

		// What dispensing decides. The server copies the Rx from the prescription; of a glasses job's
		// Rx only the PD and fitting heights are read.
		function jobToApi(job) {
			const contacts = isContacts(job);
			return {
				prescription_id: job.rxId,
				spectacle_rx: contacts ? null : spectacleRxToApi(job.rx),
				fitting_fee: contacts && job.fitting ? job.fitting.fee : null,
				glasses_use: contacts ? null : job.use || null,
				frame: contacts ? null : frameToApi(job.frame),
				lens: contacts ? null : jobLensToApi(job.lens),
				extras: contacts ? null : extrasToApi(job.extras),
				trace: contacts ? null : traceToApi(job.trace),
				supply: contacts ? supplyToApi(job.supply, job) : null,
				custom_items: (job.customItems || [])
					.filter((i) => i && (i.name || "").trim())
					.map((i) => ({ name: (i.name || "").trim(), price: num(i.price) || 0, code: i.code || null })),
				billing: { discount: num(job.billing.discount) || 0, discount_type: job.billing.discountType, payment_option: job.billing.paymentOption },
				order: {
					notes: (job.order && job.order.notes) || null, reorder_of_job_id: (job.order && job.order.reorderOf) || null,
					document_id: (job.order && job.order.documentId) || null,
				},
			};
		}

		// ------------------------------------------------------------------
		// Jobs (from the API)
		// ------------------------------------------------------------------
		// A patient's jobs, newest first. Kept in place, so a tile holding the list sees every change.
		const jobsByPatient = {};
		const patientJobs = (patientId) => jobsByPatient[patientId] || (jobsByPatient[patientId] = []);

		function upsert(patientId, job) {
			const list = patientJobs(patientId);
			const i = list.findIndex((j) => j.id === job.id);
			if (i >= 0) list[i] = job;
			else list.unshift(job);
			$rootScope.$broadcast("lensDispensing:jobs", patientId);
			return job;
		}

		function loadPatientJobs(patientId) {
			return $q.all([loadCatalogue().catch(angular.noop), loadSettings().catch(angular.noop)])
				.then(() => LensDispensingAPI.getPatientJobs(patientId))
				.then(function (resp) {
					const list = patientJobs(patientId);
					list.length = 0;
					(resp.data || []).forEach((r) => list.push(jobFromApi(r)));
					return list;
				});
		}

		// Every job in the practice with its patient, for the practice job list.
		function loadPracticeJobs() {
			return $q.all([loadCatalogue().catch(angular.noop), loadSettings().catch(angular.noop)])
				.then(() => LensDispensingAPI.getPracticeJobs())
				.then((resp) => (resp.data || []).map((r) => ({
					job: jobFromApi(r),
					patient: { id: r.patient_id, firstName: r.patient_first_name, lastName: r.patient_last_name },
				})));
		}

		// Creates or updates the job. Resolves to the job as the server keeps it.
		function saveJob(patientId, job) {
			const body = jobToApi(job);
			const request = job.id ? LensDispensingAPI.updateJob(job.id, body) : LensDispensingAPI.createJob(body);
			return request.then((resp) => upsert(patientId, jobFromApi(resp.data)));
		}

		// Saves the quote, then invoices it with the amounts shown to the patient.
		// The lines as the invoice carries them: the job's, then its discount, which has no item.
		function invoiceLines(job) {
			const lines = jobLines(job).map((l) => ({ kind: l.kind, code: l.code || null, item_name: l.itemName || null, name: l.name, price: l.price, quantity: l.qty || null }));
			const t = totals(job);
			if (t.discount) {
				const percent = job.billing && job.billing.discountType === "percent";
				lines.push({ kind: "Discount", code: null, item_name: null, name: percent ? `Discount (${job.billing.discount}%)` : "Discount", price: -t.discount, quantity: null });
			}
			return lines;
		}

		// What is still owing on an invoiced job, and what of that is due now (the deposit, or the whole).
		const owing = (job) => (job && job.invoice ? Math.max(0, Math.round(((job.invoice.total || 0) - (job.invoice.paid || 0)) * 100) / 100) : 0);
		const dueNow = (job) => (job && job.invoice ? Math.min(owing(job), Math.max(0, Math.round(((job.invoice.dueNow || 0) - (job.invoice.paid || 0)) * 100) / 100)) : 0);

		/**
		 * Takes a payment against an invoiced job - recorded in Xero too where the practice bills there.
		 * @param {Object} payment - { amount, method, accountId }
		 */
		function recordPayment(patientId, job, payment) {
			return LensDispensingAPI.recordPayment(job.id, {
				amount: payment.amount, method: payment.method, account_id: payment.accountId || null, date_paid: fmtDate(new Date()),
			}).then((resp) => upsert(patientId, jobFromApi(resp.data)));
		}

		// Whether the practice raises its invoices in Xero.
		const billsThroughXero = () => !!(Session.practice && Session.practice.practiceFeatures && Session.practice.practiceFeatures.billingFeature);

		// An invoice kept in CatTrax only at a practice billing through Xero - Xero couldn't be used when
		// the job was invoiced - which can still be raised there.
		const waitsForXero = (job) => billsThroughXero() && !!job && !!job.invoice && !job.invoice.xeroId;

		// What the user must be told once a job is invoiced: only an invoice left as a draft in Xero.
		function invoicedNotice(job, patientName, emailed) {
			const inv = job.invoice || {};
			if (inv.xeroId && inv.xeroStatus === "DRAFT") {
				return {
					message: `Invoice ${inv.number || ""} is in Xero but couldn't be ${emailed ? "emailed" : "approved"} - it's waiting as a draft in the patient's invoices. Any stock on the job comes off inventory once it's approved.`,
					className: "alert-warning",
				};
			}
			return null;
		}

		/**
		 * Invoices the job. A practice billing through Xero raises it there - emailed to the patient by
		 * Xero when `emailToPatient`, otherwise only approved - and the job keeps its invoice number; or,
		 * with `keepOutOfXero`, keeps it in CatTrax to raise in Xero later (raiseInXero).
		 *
		 * Invoiced is ready to order, so the server makes the order PDF now - for the lab or supplier named
		 * here - and files it in the patient's documents, where it can go straight away. Sending the order
		 * makes it again with the supplier's reference. A PDF that can't be made never undoes the invoice:
		 * the job comes back with `$orderPdfFailed` set, and marking the order sent makes it.
		 */
		function invoiceJob(patientId, job, sentTo, emailToPatient, keepOutOfXero) {
			const t = totals(job);
			return saveJob(patientId, job)
				.then((saved) => LensDispensingAPI.invoiceJob(saved.id, {
					invoice_number: null, total: t.total, amount_due_now: t.deposit, sent_to: sentTo || null,
					email_to_patient: !!emailToPatient, raise_in_xero: !keepOutOfXero, lines: invoiceLines(saved), lab_name: supplierFor(saved) || null,
				}))
				.then(function (resp) {
					const invoiced = upsert(patientId, jobFromApi(resp.data));
					if (!invoiced.order.generatedDocumentId) return angular.extend(invoiced, { $orderPdfFailed: true });
					$rootScope.$broadcast("lensDispensing:document", patientId);
					return invoiced;
				});
		}

		/**
		 * Raises in Xero the invoice of a job kept in CatTrax only, with the lines it was invoiced with. Any
		 * payments already taken go into Xero with it, into `accountId`. The job's status doesn't change.
		 */
		function raiseInXero(patientId, job, accountId) {
			return LensDispensingAPI.invoiceJob(job.id, { email_to_patient: false, payments_account_id: accountId || null })
				.then((resp) => upsert(patientId, jobFromApi(resp.data)));
		}

		// The status after the job's own, as the server moves it; null once collected. A contact lens
		// supply shipped to the patient never comes through the practice.
		function nextStatus(job) {
			const order = STATUSES.map((s) => s.key);
			if (job.status === "COLLECTED" || job.status === "QUOTE" || job.status === "DRAFT") return null;
			if (homeDelivery(job) && !isCustom(job) && job.status === "IN_PRODUCTION") return "COLLECTED";
			return order[order.indexOf(job.status) + 1] || null;
		}

		// The button that moves a job on, worded for its pathway.
		// The same object every time: templates watch the action itself (ng-if="nextAction(job)"), and a
		// fresh object on each call never settles - an infinite digest that stalls the whole page.
		const NEXT_ACTIONS = {
			// CatTrax makes the order PDF; the practice emails or uploads it to the lab and marks it sent.
			send: { label: "Mark order sent", icon: "fa-check", next: "ORDERED" },
			dispatched: { label: "Dispatched", icon: "fa-truck", next: "IN_PRODUCTION" },
			labAccepted: { label: "Lab accepted", icon: "fa-cogs", next: "IN_PRODUCTION" },
			received: { label: "Received", icon: "fa-inbox", next: "RECEIVED" },
			delivered: { label: "Delivered", icon: "fa-home", next: "COLLECTED" },
			collected: { label: "Collected", icon: "fa-check", next: "COLLECTED" },
		};

		function nextActionFor(job) {
			const contacts = isContacts(job) && !isCustom(job);
			switch (nextStatus(job)) {
				case "ORDERED": return NEXT_ACTIONS.send;
				case "IN_PRODUCTION": return contacts ? NEXT_ACTIONS.dispatched : NEXT_ACTIONS.labAccepted;
				case "RECEIVED": return NEXT_ACTIONS.received;
				case "COLLECTED": return homeDelivery(job) && !isCustom(job) ? NEXT_ACTIONS.delivered : NEXT_ACTIONS.collected;
			}
			return null;
		}

		// The status one step back, for undoing a mis-clicked status button. Null where undo makes no
		// sense: a quote, or an invoiced job (un-invoicing is not a status change). It mirrors
		// nextStatus, so a contact lens supply shipped to the patient steps back over Received.
		function previousStatus(job) {
			if (!job || statusIndex(job.status) <= statusIndex("INVOICED")) return null;
			if (job.status === "COLLECTED" && homeDelivery(job) && !isCustom(job)) return "IN_PRODUCTION";
			return STATUSES[statusIndex(job.status) - 1].key;
		}

		// Where undo puts the job back to, worded for its pathway - "" when it can't go back.
		function undoLabel(job) {
			const prev = previousStatus(job);
			return prev ? statusFor(angular.extend({}, job, { status: prev })).label : "";
		}

		// Puts the job back one status - a mis-click undone. The server clears the dates the undone
		// step had set; stock already taken stays taken (it isn't taken twice on the way forward).
		function revertJob(patientId, job) {
			const prev = previousStatus(job);
			if (!prev) return $q.reject({ data: { clientMessage: "This job can't go back a step." } });
			return LensDispensingAPI.advanceJob(job.id, { status: prev }).then((resp) => upsert(patientId, jobFromApi(resp.data)));
		}

		/**
		 * Moves the job on to its next status. Marking the order sent - the practice sends the PDF itself -
		 * has the server make the order PDF again,
		 * with the supplier's reference, and file it in the patient's documents in place of the one made at
		 * invoicing; the order isn't sent if the PDF can't be made.
		 * @param {Object} [details] - { reference } - the lab or supplier's order reference, when sending.
		 */
		function advanceJob(patientId, job, details) {
			const next = nextStatus(job);
			if (!next) return $q.reject({ data: { clientMessage: "This job can't be moved on." } });
			const change = { status: next };
			if (next === "ORDERED") {
				angular.extend(change, { lab_name: supplierFor(job), reference: (details && details.reference) || null, date_due: fmtDate(addDays(new Date(), leadDaysFor(job))) });
			}
			return LensDispensingAPI.advanceJob(job.id, change).then(function (resp) {
				const saved = upsert(patientId, jobFromApi(resp.data));
				// The patient's documents list now has the order PDF in it.
				if (next === "ORDERED") $rootScope.$broadcast("lensDispensing:document", patientId);
				return saved;
			});
		}

		function addNote(patientId, job, text) {
			return LensDispensingAPI.addNote(job.id, text).then((resp) => upsert(patientId, jobFromApi(resp.data)));
		}

		function removeJob(patientId, job) {
			return LensDispensingAPI.deleteJob(job.id).then(function () {
				const list = patientJobs(patientId);
				const i = list.findIndex((j) => j.id === job.id);
				if (i >= 0) list.splice(i, 1);
				$rootScope.$broadcast("lensDispensing:jobs", patientId);
			});
		}

		// Whether the job has an order PDF: the server's, or one an older job had uploaded to the patient's files.
		const hasOrderDocument = (job) => !!(job && job.order && (job.order.generatedDocumentId || job.order.documentId));

		// The job's order PDF. Resolves to a Blob.
		function orderDocument(patientId, job) {
			if (!hasOrderDocument(job)) return $q.reject({ data: { clientMessage: "This job has no order PDF." } });
			const request = job.order.generatedDocumentId
				? PatientAPI.downloadDocument(patientId, job.order.generatedDocumentId, "LENS_ORDER")
				: UploadFileAPI.downloadFile(patientId, job.order.documentId);
			return request.then((resp) => new Blob([resp.data], { type: "application/pdf" }));
		}

		// A message for a failed request: one this service made itself, or the server's refusal where it gave one.
		function errorMessage(resp, fallback) {
			if (resp && resp.data && resp.data.clientMessage) return resp.data.clientMessage;
			const message = resp && resp.data && resp.data.message;
			const reason = message && /CattraxServiceException: (.*?)\]?$/.exec(message);
			return (reason && reason[1]) || (resp && resp.status === 403 ? "You don't have permission to do that." : fallback);
		}

		// ------------------------------------------------------------------
		// Contact lenses from inventory
		// ------------------------------------------------------------------
		// A practice's contact lenses are its Contact lenses inventory items. The item is the lens - its
		// maker, material, design and replacement (cl_*) - and each variation is one box prescription:
		// sphere, cylinder, axis, add, base curve and diameter, with the box size as its units per pack.
		// Most have none in stock: lenses are ordered in for the patient. A prescription is written for a
		// box the practice has there, in stock or not. An item from before inventory held prescriptions
		// has only a power, read from its size, and its type and replacement are guessed from its name.
		// Made-to-order lenses come from the lens catalogue, which is also where a new inventory lens
		// starts from.
		const clStock = { items: [] };
		let clStockPromise = null;
		let clStockPracticeId = null;
		const CL_STOCK_PAGE_SIZE = 25;
		const CL_STOCK_MAX_PAGES = 40;

		// "-2.25", "+1.50D" or "-2.25 / BC 8.6" - the power a variation is for, or null.
		const parsePower = (size) => {
			const m = /^\s*([+-]?\d{1,2}(?:\.\d{1,2})?)\s*D?\b/i.exec(String(size || ""));
			return m ? Number(m[1]) : null;
		};
		const guessModality = (text) =>
			/\b(1[- ]?day|daily|dailies|one[- ]?day)\b/i.test(text) ? "daily"
				: /\b(2[- ]?week|two[- ]?week|bi[- ]?weekly|fortnight)/i.test(text) ? "fortnightly" : "monthly";
		const guessDesign = (text) =>
			/toric|astig/i.test(text) ? "toric" : /multifocal|presbyop|progressive|bifocal/i.test(text) ? "multifocal" : "spherical";

		// A box's prescription, from its inventory variation. No cylinder is none, whatever the axis says.
		function clRxOf(v) {
			const cyl = num(v.cl_cylinder) || null;
			return { sph: num(v.cl_sphere), cyl: cyl, axis: cyl ? num(v.cl_axis) : null, add: v.cl_add || null, bc: num(v.cl_base_curve), dia: num(v.cl_diameter) };
		}
		// A box's prescription as its inventory variation's fields.
		const clRxFields = (rx) => ({
			cl_sphere: num(rx.sph), cl_cylinder: num(rx.cyl) || null, cl_axis: num(rx.cyl) ? num(rx.axis) : null,
			cl_add: String(rx.add || "").trim() || null, cl_base_curve: num(rx.bc), cl_diameter: num(rx.dia),
		});

		// One box's prescription in a line, as its variation is labelled in inventory:
		// "-2.25 / -1.25 × 180 · Add MED · BC 8.6 · DIA 14.5".
		function clRxText(rx) {
			let s = fmtPower(rx.sph);
			if (num(rx.cyl)) s += ` / ${fmtPower(rx.cyl)} × ${rx.axis || "?"}`;
			if (rx.add) s += ` · Add ${isNum(rx.add) ? fmtPower(rx.add) : rx.add}`;
			if (isNum(rx.bc)) s += ` · BC ${Number(rx.bc).toFixed(Math.round(Number(rx.bc) * 100) % 10 ? 2 : 1)}`;
			if (isNum(rx.dia)) s += ` · DIA ${Number(rx.dia).toFixed(1)}`;
			return s;
		}

		// Whether a box is a prescription: the same powers and add, and - `fitToo` - base curve and diameter.
		// An add is compared as written, a number in any form ("2", "+2.00") as its power.
		const sameNum = (a, b) => (isNum(a) ? isNum(b) && Math.abs(Number(a) - Number(b)) < 0.001 : !isNum(b));
		const addKey = (a) => {
			const s = String(a === null || a === undefined ? "" : a).trim();
			return /^[+-]?\d*\.?\d+$/.test(s) ? fmtPower(Number(s)) : s.replace(/\s+/g, " ").toUpperCase();
		};
		function sameBox(rx, e, fitToo) {
			const cyl = (x) => num(x.cyl) || null;
			if (!sameNum(rx.sph, e.sph) || !sameNum(cyl(rx), cyl(e))) return false;
			if (cyl(rx) && !sameNum(rx.axis, e.axis)) return false;
			if (addKey(rx.add) !== addKey(e.add)) return false;
			return !fitToo || (sameNum(rx.bc, e.bc) && sameNum(rx.dia, e.dia));
		}

		const distinct = (list) => list.filter((x, i) => x !== null && x !== undefined && list.indexOf(x) === i);

		function contactLensFromInventory(item) {
			const name = (item.name || "").trim();
			// An archived box can't be prescribed or dispensed; an archived lens offers none.
			const variations = InventoryAPI.activeVariations(item).map(function (v) {
				const rx = clRxOf(v);
				return { variationId: v.variation_id, size: v.size || "", rx: rx, power: rx.sph !== null ? rx.sph : parsePower(v.size), stock: v.quantity || 0, barcode: v.barcode || "" };
			});
			// Held as box prescriptions, or - an item from before - as powers in the size.
			const boxes = variations.some((v) => v.rx.sph !== null && v.rx.bc !== null);
			const powers = variations.map((v) => v.power).filter((p) => p !== null);
			const withPack = (item.variations || []).find((v) => Number(v.units_per_pack) > 0);
			const packInName = /(\d{1,3})\s*(?:pk|pack|lenses|box)\b/i.exec(name);
			// A refraction converts to the cylinders and adds the lens is made in - the catalogue lens it was
			// started from - never just those its boxes happen to have, which would over- or under-correct.
			// Without one, a cylinder rounds to 0.25 and an add stays a power.
			const made = item.cl_catalogue_id ? catalogue.contactLenses.find((c) => c.id === item.cl_catalogue_id) : null;
			return {
				id: item.item_id, inventoryItemId: item.item_id, fromInventory: true, boxes: boxes,
				// Described in inventory; an older item's type and replacement are only guessed, and can be set on the Rx.
				described: !!(item.cl_design && item.cl_modality), catalogueId: item.cl_catalogue_id || null,
				brand: "", name: name, maker: item.cl_maker || "", material: item.cl_material || "", custom: false, family: null,
				modality: item.cl_modality || guessModality(name), design: item.cl_design || guessDesign(name),
				pack: withPack ? Number(withPack.units_per_pack) : packInName ? Number(packInName[1]) : 1,
				price: Number(item.price) || 0, cost: null, leadDays: 0, features: [],
				bcs: distinct(variations.map((v) => v.rx.bc)).sort((a, b) => a - b),
				dia: (variations.find((v) => v.rx.dia !== null) || { rx: {} }).rx.dia || null,
				range: boxes && powers.length ? [Math.min.apply(null, powers), Math.max.apply(null, powers)] : [-20, 20],
				cyls: (made && made.cyls) || [],
				adds: (made && made.adds) || [],
				colours: [],
				variations: variations, stock: variations.reduce((t, v) => t + v.stock, 0),
			};
		}

		// Every Contact lenses item, each read whole for its boxes and stock. Read once per practice, or
		// again when `fresh` - a box added in inventory since. Nothing to read without the inventory
		// feature. Rejects when inventory can't be read, leaving no inventory lenses.
		function loadContactLensStock(fresh) {
			const practiceId = Session.practice && Session.practice.id;
			if (clStockPromise && clStockPracticeId === practiceId && !fresh) return clStockPromise;
			clStockPracticeId = practiceId;
			const features = (Session.practice && Session.practice.practiceFeatures) || {};
			if (!features.inventoryFeature) {
				clStock.items = [];
				clStockPromise = $q.resolve(clStock.items);
				return clStockPromise;
			}
			const listed = [];
			const page = (indexes, n) => InventoryAPI.getItemsByCategory(InventoryAPI.CATEGORY.CONTACT_LENSES, null, indexes).then(function (resp) {
				const data = resp.data || {};
				const items = Array.isArray(data.items) ? data.items : [];
				listed.push.apply(listed, items);
				const last = items.length < CL_STOCK_PAGE_SIZE || !Array.isArray(data.page_indexes) || !data.page_indexes.length || n + 1 >= CL_STOCK_MAX_PAGES;
				return last ? listed : page(data.page_indexes, n + 1);
			});
			clStockPromise = page(undefined, 0)
				.then((items) => $q.all(items.map((i) => InventoryAPI.getItem(i.item_id).then((r) => r.data, () => null))))
				.then(
					function (items) {
						clStock.items = items.filter(Boolean).map(contactLensFromInventory).sort((a, b) => a.name.localeCompare(b.name));
						return clStock.items;
					},
					function (resp) {
						clStock.items = [];
						clStockPromise = null;
						return $q.reject(resp);
					}
				);
			return clStockPromise;
		}

		// The lenses a contact lens prescription is written for: the practice's from inventory, then the
		// catalogue's made-to-order ones. The catalogue's stock lenses are templates for adding a lens to
		// inventory, not offered here.
		const contactLensChoices = () => clStock.items.concat(catalogue.contactLenses.filter((c) => c.custom));

		// The box of a lens with a prescription's powers and add - with its base curve too where the lens
		// comes in more than one - or null. For matching a refraction, whose fit isn't known yet.
		function boxFor(lens, rx) {
			if (!lens || !lens.boxes || !rx || !isNum(rx.sph)) return null;
			const same = lens.variations.filter((v) => sameBox(v.rx, rx, false));
			return same.find((v) => sameNum(v.rx.bc, rx.bc)) || same[0] || null;
		}

		// The inventory variation for an eye's prescription - every parameter, the box it is - with what's
		// on hand, or null when the lens isn't from inventory or has no such box. An older item knows only
		// its powers.
		function stockFor(product, eye) {
			if (!product || !product.inventoryItemId || !eye || !isNum(eye.sph)) return null;
			const item = clStock.items.find((c) => c.inventoryItemId === product.inventoryItemId);
			if (!item) return null;
			if (!item.boxes) return item.variations.find((v) => v.power !== null && Math.abs(v.power - Number(eye.sph)) < 0.001) || null;
			return item.variations.find((v) => sameBox(v.rx, eye, true)) || null;
		}

		// A new glasses or contact lens job, not yet saved.
		function newJob(type) {
			const base = {
				id: null, created: new Date(), status: "DRAFT", rxId: null, invoice: null, order: { notes: "" }, notes: [], customItems: [],
			};
			if (type === "contacts") {
				return angular.extend(base, {
					type: "contacts", product: null, productOs: null, cl: emptyClRx(), fitting: emptyFitting(), supply: emptySupply(),
					billing: { discount: 0, discountType: "amount", paymentOption: "full" },
				});
			}
			return angular.extend(base, {
				frame: null, lens: null, rx: emptyRx(), use: null, extras: emptyExtras(), trace: null,
				billing: { discount: 0, discountType: "amount", paymentOption: "deposit" },
			});
		}

		const service = {
			LENS_DESIGNS,
			TINT_DENSITIES,
			STATUSES,
			CL_MODALITIES,
			CL_DESIGNS,
			CL_SUPPLY_MONTHS,
			CL_COLOURS,
			gstRate,
			frameFromInventory,
			setFrameColour,
			frameName,
			ownFrame,
			otherFrame,
			madeToOrderLens,
			frameColourHex,
			byId,
			emptyRx,
			emptyExtras,
			extrasLines,
			jobLines,
			totals,
			statusMeta,
			statusIndex,
			isLocked,
			statusLabel,
			statusIcon,
			trackFor,
			trackIndex,
			supplierFor,
			leadDaysFor,
			addDays,
			labFor: (lens) => (lens ? LABS[lens.supplier] || `${lens.supplier} lab` : ""),
			isContacts,
			isCustom,
			customEye,
			customSpec,
			orderPowerFromTrial,
			snapClPower,
			clPowers,
			colourHex,
			supplyText,
			homeDelivery,
			modalityMeta,
			emptyClRx,
			emptyFitting,
			emptySupply,
			lensFor,
			lensEyes,
			lensPriceFor,
			transpose,
			toMinusCyl,
			boxesFor,
			supplyDays,
			supplyEnds,
			toContactRx,
			toCustomRx,
			contactsLines,
			fmtPower,
			fmtPrism,
			fmtMeasurements,
			emptyEye,
			lensPowers,
			ADD_LIMITS,
			addProblem,
			USE_WORDS,
			keyDate,
			isOverdue,
			parseDate,
			fmtDate,
			jobTitle,
			errorMessage,

			// The practice's dispensing extras.
			loadSettings,
			applySettings,
			extra,
			extraHex: (list, id) => (extra(list, id) || {}).hex || null,

			// Billing items for the hand-added invoice lines.
			searchBillingItems,
			billingItemLabel,

			// The catalogue.
			loadCatalogue,
			lenses: () => catalogue.lenses,
			contactLenses: () => catalogue.contactLenses,
			contactLensById,
			loadContactLensStock,
			contactLensChoices,
			stockFor,
			boxFor,
			clRxOf,
			clRxFields,
			clRxText,

			// Rx shapes, shared with LensPrescribingStore.
			spectacleRxFromApi,
			spectacleRxToApi,
			contactLensRxFromApi,
			contactLensRxToApi,
			productFromApi,
			fittingFromApi,
			fittingToApi,
			syncTrial,

			// Jobs.
			newJob,
			getJobs: patientJobs,
			loadPatientJobs,
			loadPracticeJobs,
			saveJob,
			invoiceJob,
			raiseInXero,
			billsThroughXero,
			waitsForXero,
			invoicedNotice,
			owing,
			dueNow,
			recordPayment,
			nextActionFor,
			advanceJob,
			previousStatus,
			undoLabel,
			revertJob,
			addNote,
			removeJob,
			orderDocument,
			hasOrderDocument,

			// A collected contact lens job whose supply runs out within the month - time to reorder.
			reorderDue: function (job) {
				const ends = supplyEnds(job);
				return !!ends && job.status === "COLLECTED" && ends < addDays(new Date(), 30);
			},

			// A new contact lens job repeating a previous one: same prescription and supply, no fitting fee.
			reorderFrom: function (job) {
				return angular.extend(newJob("contacts"), {
					ref: "Reorder",
					rxId: job.rxId,
					product: angular.copy(job.product),
					productOs: angular.copy(job.productOs || null),
					cl: angular.copy(job.cl),
					fitting: angular.extend(angular.copy(job.fitting), { fee: "none" }),
					supply: angular.extend(angular.copy(job.supply), { autoReorder: true }),
					order: { notes: "", reorderOf: job.id },
				});
			},

			// The dialog that creates and edits a job of this pathway.
			dialogFor: function (job, type) {
				return (job ? isContacts(job) : type === "contacts")
					? { controller: "lensContactsModalController", templateUrl: "app/src/js/components/lensDispensing/lens-contacts-modal.tmpl.html" }
					: { controller: "lensDispensingModalController", templateUrl: "app/src/js/components/lensDispensing/lens-dispensing-modal.tmpl.html" };
			},
		};

		// The extras the dialogs offer: the practice's, offered ones only. Read through getters, as
		// they change when the settings are read.
		Object.defineProperties(service, {
			LENS_TYPES: { get: () => active.lensTypes },
			TINTS: { get: () => active.tints },
			COATINGS: { get: () => active.coatings },
			MIRRORS: { get: () => active.mirrors },
			ACCESSORIES: { get: () => active.accessories },
			CL_FITTINGS: { get: () => active.fittingFees },
			CL_CARE: { get: () => active.careProducts },
			CL_DELIVERY: { get: () => delivery },
			CL_ANNUAL_DISCOUNT: { get: () => EXTRAS.annualSupplyDiscountPercent },
			GRADIENT_PRICE: { get: () => EXTRAS.gradientPrice },
		});

		return service;
	},
]);
